import { Component, inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { __, cornflower, FormatterService, green, MessengerService, ModalComponent, red } from '@web/birdz-angular';
import { fontFamily } from 'app/config/constants.config';
import { PdsKpiHistoryPoint } from 'app/interfaces/pds-kpi-history.interface';
import { Api } from 'app/services/api.service';
import { FilterService } from 'app/services/filter.service';
import { KpiType } from 'app/types/kpi-type.type';
import { format, Locale, parseISO } from 'date-fns';
import { enUS, fr } from 'date-fns/locale';
import Highcharts from 'highcharts';
import { HighchartsChartComponent } from 'highcharts-angular';
import { EMPTY, finalize, tap } from 'rxjs';

export interface ModalKpiPdsHistoryData {
  pdsCustomerId: string;
  kpiLabel: string;
  kpiType: KpiType;
  // 'state': OK/KO area, 'rate': rate area in %
  display: 'state' | 'rate';
  // 'day': one point per day from `from` to `to`, 'hour': every slot of the `to` day
  granularity?: 'hour' | 'day';
  hourlyPeriod?: number | null; // in minutes, for the hourly granularity
  pdsId: string;
  kpi: string;
  from: Date;
  to: Date;
}

@Component({
  selector: 'app-modal-kpi-pds-history',
  imports: [ModalComponent, HighchartsChartComponent, MatProgressSpinnerModule, TranslocoPipe],
  templateUrl: './modal-kpi-pds-history.component.html',
  styleUrl: './modal-kpi-pds-history.component.scss',
})
export class ModalKpiPdsHistoryComponent implements OnInit {
  data: ModalKpiPdsHistoryData = inject(MAT_DIALOG_DATA);
  private api = inject(Api);
  private filterService = inject(FilterService);
  private messenger = inject(MessengerService);
  private transloco = inject(TranslocoService);
  private formatter = inject(FormatterService);

  Highcharts = Highcharts;
  chartOptions?: Highcharts.Options;
  isLoading = false;

  ngOnInit(): void {
    this.isLoading = true;
    this.api
      .fetchPdsKpiHistory(
        this.filterService.toParams(),
        this.data.pdsId,
        this.data.kpi,
        this.data.from,
        this.data.to,
        this.data.granularity ?? 'day'
      )
      .pipe(
        tap((history) => this.buildChart(history.points)),
        this.messenger.showFriendlyErrorMessage(EMPTY),
        finalize(() => (this.isLoading = false))
      )
      .subscribe();
  }

  private getLocale(): Locale {
    return this.transloco.getActiveLang() === 'fr' ? fr : enUS;
  }

  private buildChart(points: PdsKpiHistoryPoint[]): void {
    const locale = this.getLocale();

    const categories = points.map((p) => p.date);
    const values = points.map((p) => p.value);
    const isRate = this.data.display === 'rate';
    const isHourly = this.data.granularity === 'hour';
    const formatRate = (value: number) => this.formatter.number(value, '1.2-2') + ' %';

    this.chartOptions = {
      credits: { enabled: false },
      title: { text: undefined },
      chart: {
        type: 'area',
        style: { fontFamily },
      },
      xAxis: {
        categories,
        labels: {
          formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
            const date = parseISO(this.value as string);
            return format(date, isHourly ? 'HH:mm' : 'dd/MM', { locale });
          },
        },
        tickInterval:
          isHourly && this.data.hourlyPeriod
            ? 60 / this.data.hourlyPeriod // one tick per hour
            : Math.max(1, Math.floor(points.length / 8)),
      },
      yAxis: isRate
        ? {
            title: { text: __('RATE') },
            min: 0,
            max: 100,
            labels: { format: '{value} %' },
          }
        : {
            title: { text: __('KPI_DETAILS.PDS_KPI_HISTORY.CHART_TITLE') },
            tickPositions: [0, 1],
            labels: {
              formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
                return this.value === 1 ? __('KPI_OK') : __('KPI_KO');
              },
            },
          },
      tooltip: {
        formatter: function (this: Highcharts.Point) {
          const date = parseISO(String(this.category));
          const dateStr = format(date, isHourly ? 'dd/MM/yyyy HH:mm' : 'dd/MM/yyyy', { locale });
          const y = this.y;
          if (y == null) {
            return `<b>${dateStr}</b><br/>-`;
          }
          if (isRate) {
            return `<b>${dateStr}</b><br/>${formatRate(y)}`;
          }
          const color = y === 1 ? green : red;
          const label = y === 1 ? __('KPI_OK') : __('KPI_KO');
          return `<b>${dateStr}</b><br/><span style="color:${color}">${label}</span>`;
        },
        useHTML: true,
      },
      legend: { enabled: false },
      lang: { noData: __('NO_DATA_AVAILBLE_FOR_THIS_PERIOD') },
      noData: {
        style: { fontWeight: 'normal', fontSize: '16px', color: '#00000099' },
      },
      plotOptions: {
        series: {
          animation: false,
          color: cornflower,
          marker: { enabled: false },
          lineWidth: 2,
          connectNulls: false,
        },
        area: {
          fillOpacity: 0.2,
        },
      },
      series: [
        {
          type: 'area',
          name: this.data.kpiLabel,
          data: values,
        },
      ],
    };
  }
}
